import { get, values } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { BACKGROUND_TYPES, OVERLAY_POSITIONS } from 'constants/landingPage';
import { mediumUp } from 'constants/mediaQueries';
import HeroSlideModel from 'models/HeroSlide';
import Rudderstack from 'utils/Rudderstack';
import { cn } from 'utils/Tailwind';

import BackgroundImage from './BackgroundImage';
import BackgroundVideo from './BackgroundVideo';

const HeroSlide = ({ data, overlayPositionDesktop, overlayPositionMobile }) => {
  const { backgroundAsset, backgroundType, ctaLinks, eyebrow, heading, isInverted, subheading } =
    data;

  const isMediumUp = useMediaQuery({ query: mediumUp });

  const _backgroundType = isMediumUp ? backgroundType.landscape : backgroundType.portrait;
  const _overlayPosition = isMediumUp ? overlayPositionDesktop : overlayPositionMobile;

  const heroImageName = isMediumUp
    ? get(data, ['_data', 'heroAssetLandscape', 'title'])
    : get(data, ['_data', 'heroAssetPortrait', 'title']);

  const trackCTAClick = useCallback(
    ({ url, text, event }) => {
      event?.preventDefault();
      event?.stopPropagation();

      Rudderstack.navigationDelayForIphoneAndSafari({
        analyticsFn: () => {
          Rudderstack.homepageHeroClick({
            heroImageName,
            linkCTAName: text,
            linkDestination: url,
          });
        },
        urlToNavigate: url,
      });
      return true;
    },
    [heroImageName]
  );

  return (
    <div className="HeroSlide">
      <div className="BGAsset absolute inset-0">
        {_backgroundType === BACKGROUND_TYPES.VIDEO ? (
          <BackgroundVideo video={backgroundAsset} />
        ) : (
          <BackgroundImage image={backgroundAsset} />
        )}
      </div>

      <div className={cn('Overlay absolute inset-0 flex', {})}>
        <div
          className={cn('Content flex flex-1 flex-col p-6 md:p-10', {
            'items-center justify-center text-center':
              _overlayPosition === OVERLAY_POSITIONS.CENTER,
            'items-end justify-end text-right': _overlayPosition === OVERLAY_POSITIONS.BOTTOM_RIGHT,
            'items-start justify-end text-left': _overlayPosition === OVERLAY_POSITIONS.BOTTOM_LEFT,
            'text-white': !isInverted,
            'text-black': isInverted,
          })}
        >
          {eyebrow && (
            <p className="Eyebrow text-sm font-bold uppercase leading-7 tracking-widest md:leading-8">
              {eyebrow}
            </p>
          )}

          <h1 className="text-3xl font-bold md:text-4xl lg:text-5xl">{heading}</h1>

          {subheading && (
            <p className="mt-2 max-w-3xl whitespace-pre-line text-base/tight font-bold tracking-wider md:mt-3">
              {subheading}
            </p>
          )}

          {ctaLinks?.length > 0 && (
            <div
              className={cn('CTALinks mt-8 flex gap-2 sm:gap-4', {
                'absolute inset-x-4 bottom-4 w-auto md:static':
                  _overlayPosition === OVERLAY_POSITIONS.CENTER && !isMediumUp,
                'w-full md:static md:mt-8':
                  _overlayPosition !== OVERLAY_POSITIONS.CENTER && !isMediumUp,
              })}
            >
              {ctaLinks.slice(0, 2).map(({ url, text }) => (
                <a
                  href={url}
                  className={cn(
                    `flex-1 whitespace-pre border bg-transparent bg-white px-4 py-4 text-center text-xs/none font-medium uppercase tracking-widest text-black transition-colors duration-300 ease-in-out hover:bg-black hover:text-white sm:min-w-44 sm:flex-auto md:px-6 md:text-sm/none`,
                    [!isInverted && 'text-black']
                  )}
                  key={text}
                  onClick={event => trackCTAClick({ event, text, url })}
                >
                  {text}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

HeroSlide.propTypes = {
  data: PropTypes.instanceOf(HeroSlideModel).isRequired,
  overlayPositionDesktop: PropTypes.oneOf(values(OVERLAY_POSITIONS)),
  overlayPositionMobile: PropTypes.oneOf(values(OVERLAY_POSITIONS)),
  openVideoModal: PropTypes.func,
  openInlineVideo: PropTypes.func,
  videoUrl: PropTypes.string,
};

HeroSlide.defaultProps = {
  openVideoModal: () => {},
};

export default HeroSlide;
